import icon from "./location.inline.svg";
import icons from "./locations.inline.svg";
import clusterIcon from "./spider.inline.svg";

import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet/dist/leaflet.css";
import * as Leaflet from "leaflet";
import { divIcon, Point as LeafletPoint } from "leaflet";
import {
  // @ts-ignore
  MarkerClusterGroup,
  // @ts-ignore
  MarkerCluster,
  // @ts-ignore
  MarkerClusterSpiderfyEvent,
} from "leaflet.markercluster";

import { MapSet, Map, Point, PointId, PointData } from "src/js/models/map";
import { range, isEqual, shuffle } from "lodash-es";
import { array, nonEmptyArray, option, record } from "fp-ts";
import { Option } from "fp-ts/lib/Option";
import { pipe, tuple } from "fp-ts/function";
import { allEqual } from "src/js/logic/functions";
import path from "path";
import { PathChoice } from "../parsers/moduleFilterParser";

export type MapViewEvent = {
  type: "clickSingle" | "clickMultiple" | "mapChanged";
  eventData: any;
};

type ugPointData = {
  point: Point;
};

export class UGMarker extends Leaflet.Marker {
  ug: ugPointData;

  constructor(
    latLng: L.LatLngExpression,
    ug: ugPointData,
    options?: L.MarkerOptions
  ) {
    super(latLng, options);
    this.ug = ug;
  }
}

export class MapView {
  static render(
    mapViewElement: HTMLElement,
    mapSet: MapSet,
    points: Array<Point>,
    showTooltips: boolean,
    clickSingleHandler: (pointId: PointId) => any,
    clickMultipleHandler: (points: Point[]) => any
  ): Leaflet.Map {
    const initialMapId = mapSet.initialMap.id;

    const isValidPoint = (p: Point) =>
      Number.isInteger(p.x) && Number.isInteger(p.y);

    points = array.filter(isValidPoint)(JSON.parse(JSON.stringify(points)));

    const markerFromPoint = (map: Map) => (point: Point) => {
      const marker = new UGMarker(
        [map.height - point.y, point.x],
        {
          point: point,
        },
        {
          icon: MapView.IconFromPoint(point, showTooltips),
        }
      );
      const pointIdObject: PointId = { id: point.id };
      marker.on("click", () => clickSingleHandler(pointIdObject));
      return marker;
    };

    const mapsWithData = pipe(
      mapSet.maps,
      array.map((map) => {
        const markers = pipe(
          points,
          array.filter((point) => point.mapId === map.id)
        );
        const pair: [string, { map: Map; points: Point[] }] = [
          map.order.toString(),
          { map, points: markers },
        ];
        return pair;
      }),
      record.fromEntries,
      // turn points and maps into leaflet markers and leaflet image-layers
      record.map(({ map, points }) => {
        const mapImageLayer = Leaflet.imageOverlay(
          map.imageURL,
          MapView.MakeLeafletMapBounds(map)
        );
        const markers = array.map(markerFromPoint(map))(points);
        const clusterLayer = MapView.MakeClusterLayer(
          clickSingleHandler,
          clickMultipleHandler,
          showTooltips,
          map,
          markers
        );
        const mapLayer = Leaflet.layerGroup([mapImageLayer, clusterLayer]);

        mapLayer.on("add", (e) => {
          const leafletMap = e.target._map;
          leafletMap.setMaxZoom(map.maxZoom);
          leafletMap.setMinZoom(map.minZoom);
          leafletMap.fitBounds(MapView.MakeLeafletMapBounds(map));
        });
        return {
          map,
          mapLayer: mapLayer,
          points: points,
        };
      })
    );

    const namedMapLayers = pipe(
      mapsWithData,
      record.toEntries,
      array.map(([_id, data]) => tuple(data.map.title, data.mapLayer)),
      record.fromEntries
    );

    const initialMap = pipe(
      mapsWithData,
      record.toEntries,
      array.map(([_, mapWithData]) => mapWithData),
      (entries) =>
        pipe(
          entries,
          array.findFirst(
            // Get initial map if it has points
            (mapData) =>
              mapData.map.id === initialMapId && mapData.points.length > 0
          ),
          option.orElse(() =>
            // Else any map with points
            pipe(
              entries,
              array.findFirst((mapData) => mapData.points.length > 0)
            )
          ),
          option.orElse(() =>
            // Else any map with points
            pipe(
              entries,
              array.findFirst((mapData) => mapData.map.id === initialMapId)
            )
          ),
          option.orElse(() =>
            // Else any map
            pipe(entries, array.head)
          )
        )
    );

    const mapControls = Leaflet.control.layers(namedMapLayers, undefined, {
      collapsed: false,
      // hideSingleBase: true,
      position: "topright",
    });
    const numberOfLayers = record.toArray(namedMapLayers).length;

    // Setup svg based map
    const leafletMap = Leaflet.map(mapViewElement, {
      crs: Leaflet.CRS.Simple,
      attributionControl: false,
      minZoom: 0,
      maxZoom: 0,
      zoomDelta: 0.5,
      zoomSnap: 0.5,
    });

    // Debounce function
    function debounce(func: Function, wait: number) {
      let timeout: NodeJS.Timeout;
      return function (this: any, ...args: any[]) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(function () {
          func.apply(context, args);
        }, wait);
      };
    }

    // Add debounced event listener for window resize
    window.addEventListener(
      "resize",
      debounce(function (this: Window) {
        leafletMap.invalidateSize(true);
      }, 250)
    ); // Adjust the wait time as needed

    pipe(
      initialMap,
      option.map(({ mapLayer, map }) => {
        mapLayer.addTo(leafletMap); // set default map
        leafletMap.setMinZoom(map.minZoom);
        leafletMap.setMaxZoom(map.maxZoom);
        leafletMap.fitBounds(MapView.MakeLeafletMapBounds(map));
      })
    );

    if (numberOfLayers > 1) {
      mapControls.addTo(leafletMap);
    }
    return leafletMap;
  }

  private static FirstAlphabeticalRecordEntry<T>(
    r: Record<string, T>
  ): Option<T> {
    return pipe(
      r,
      record.toArray,
      array.head,
      option.map(([key, data]) => data)
    );
  }

  private static MakeLeafletMapBounds(map: Map): Leaflet.LatLngBoundsLiteral {
    return [
      [0, 0],
      [map.height, map.width],
    ];
  }

  private static MakeClusterLayer(
    clickSingleHandler: (pointId: PointId) => any,
    clickMultipleHandler: (points: Point[]) => any,
    showTooltips: boolean,
    map: Map,
    markers: Leaflet.Marker[]
  ): Leaflet.Layer {
    const markerCluster = new MarkerClusterGroup({
      maxClusterRadius: map.maxClusterRadius,
      showCoverageOnHover: false,
      iconCreateFunction: function (cluster: MarkerCluster) {
        var markers: UGMarker[] = cluster.getAllChildMarkers() as UGMarker[];

        // Bør laves med en find i stedet og ikke skifte variablens type (marker - boolean)

        const clusterIsSameLocation = pipe(
          markers,
          allEqual((a, b) => isEqual(a.getLatLng(), b.getLatLng()))
        );

        const points = markers.map((m) => {
          return m.ug.point;
        });

        if (clusterIsSameLocation) {
          return MapView.clusterIcon(points, showTooltips);
        } else {
          return MapView.clusterIconExpandable(points);
        }
      },
      spiderLegPolylineOptions: {
        opacity: 0.0,
      },
      spiderfyShapePositions: function (
        count: number,
        centerPoint: LeafletPoint
      ): LeafletPoint[] {
        centerPoint.x = -99999999;
        centerPoint.y = -99999999;
        return range(count).map((i) => centerPoint);
      },
    });
    markerCluster.addLayers(markers);

    // when we cannot zoom in anymore
    markerCluster.on("spiderfied", (event: MarkerClusterSpiderfyEvent) => {
      event.target.unspiderfy(); // deselect cluster
      const points = (event.markers as UGMarker[]).map(
        (marker) => marker.ug.point
      );
      clickMultipleHandler(points);
      return false;
    });

    return markerCluster;
  }

  private static IconFromPoint(
    point: Point,
    showTooltips: boolean
  ): Leaflet.DivIcon {
    const iconSvg = pipe(
      point.pathCustomIcon,
      option.map(
        (x) =>
          `<img src="${x.toString()}" alt="${point.locationName}" class="${
            point.iconClass
          }" />`
      ),
      option.getOrElse(() => icon)
    );

    const points = [point]; // Pakker element ind i et array

    let iconTemplate;
    if (showTooltips) {
      const elements = MapView.createElementsFromPoints(points);
      const tooltipElement = MapView.createTooltipElement();
      tooltipElement.append(...elements);
      iconTemplate = MapView.createIconWithTooltipTemplateWrapper(
        iconSvg,
        tooltipElement
      );
    } else {
      iconTemplate = MapView.createIconWithoutTooltipTemplateWrapper(iconSvg);
    }

    return Leaflet.divIcon({
      html: Leaflet.Util.template(iconTemplate, {
        iconValue: point.iconValue,
        locationName: point.locationName,
        iconClass: point.iconClass,
      }),
      className: "same-location",
      iconSize: [30, 41],
      iconAnchor: [15, 41],
    });
  }

  private static clusterIcon(
    points: Point[],
    showTooltips: boolean
  ): Leaflet.DivIcon {
    let iconsTemplate;

    if (showTooltips) {
      const elements = MapView.createElementsFromPoints(points);
      const tooltipElement = MapView.createTooltipElement();
      tooltipElement.append(...elements);
      iconsTemplate = MapView.createIconWithTooltipTemplateWrapper(
        icons,
        tooltipElement
      );
    } else {
      iconsTemplate = MapView.createIconWithoutTooltipTemplateWrapper(icons);
    }

    return divIcon({
      html: Leaflet.Util.template(iconsTemplate, {
        iconsClasses: [...new Set(points.map((p) => p.iconClass))],
        clusterSize: points.length,
      }),
      className: "ug-cluster same-location",
      iconSize: [42, 41],
      iconAnchor: [15, 41],
    });
  }
  private static clusterIconExpandable(points: Point[]): Leaflet.DivIcon {
    return divIcon({
      html: Leaflet.Util.template(clusterIcon, {
        clusterSize: points.length,
      }),
      className: "ug-cluster spider-location",
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });
  }

  private static createElementsFromPoints(points: Point[]): any[] {
    return pipe(
      points,
      array.map((point: Point) => {
        const pointData: PointData = {
          id: point.id,
          elementLogo: point.elementLogo,
          elementTitle: point.elementTitle,
        };
        const element = MapView.createClusterElement(pointData);
        return element;
      })
    );
  }

  private static createElementFromHTML(htmlString: string): HTMLElement {
    const div: HTMLDivElement = document.createElement("div");
    div.innerHTML = htmlString.trim();
    return div.firstChild as HTMLElement;
  }

  private static createClusterElement(pointData: PointData): HTMLElement {
    const htmlString = `<div class="cluster" data-id="${pointData.id}">${pointData.elementLogo}<span class="title">${pointData.elementTitle}</span></div>`;
    return MapView.createElementFromHTML(htmlString);
  }

  private static createTooltipElement() {
    const tooltipElement = MapView.createElementFromHTML(`
      <div id="clusterTooltips"></div>
    `);
    return tooltipElement;
  }

  private static createIconWithTooltipTemplateWrapper(
    iconTemplate: string,
    tooltipElement: HTMLElement
  ): string {
    const iconTemplateWrapper = `<div class="clusterIconTooltip" data-tippy-content="" data-tippy-interactive="true">${iconTemplate}${tooltipElement.outerHTML}</div>`;
    return iconTemplateWrapper;
  }

  private static createIconWithoutTooltipTemplateWrapper(
    iconTemplate: string
  ): string {
    const iconTemplateWrapper = `<div class="clusterIcon">${iconTemplate}</div>`;
    return iconTemplateWrapper;
  }
}
